import { AwsRum } from 'aws-rum-web';
import EnvironmentConfig from '../common/EnvironmentConfig';
import { localLog, setErrorSink } from '../loggers/Logger';
import RUMDOMEvents from './RUMDOMEvents';
import { UserProfile } from '@amzn/awscat-pattern-library-service-model';
import { IdType } from './RumUtils';

const DEFAULT_APPLICATION_VERSION = '1.0.1';
const DEFAULT_COOKIES_ENABLED = true;

interface RumClientProps {
  allowCookies: boolean;
  appVersion: string;
}

const SECURITY_PARAMS = [/X-Amz-Credential/, /X-Amz-Security-Token/, /X-Amz-Signature/];

type SessionAttributes = { [key: string]: string | boolean | number };

class RumClient {
  // Session attributes requested before the client exists; flushed once create() succeeds.
  private static pendingSessionAttributes: SessionAttributes = {};

  public static create(
    props: RumClientProps = {
      allowCookies: DEFAULT_COOKIES_ENABLED,
      appVersion: DEFAULT_APPLICATION_VERSION,
    },
  ) {
    if (!this.rumClient) {
      // Rum config is optional per environment
      const { rumConfig } = EnvironmentConfig.getConfig();

      if (!rumConfig) {
        // RUM disabled
        return;
      }

      const { appId, identityPoolId, guestRoleArn, region } = rumConfig;

      try {
        this.rumClient = new AwsRum(appId, props.appVersion, region, {
          sessionEventLimit: 2000, // 200 default is a bit low
          disableAutoPageView: true,
          allowCookies: props.allowCookies,
          enableXRay: true,
          identityPoolId,
          guestRoleArn,
          endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
          sessionSampleRate: 1,
          telemetries: [
            [
              'errors',
              {
                stackTraceLength: 1000,
                // @ts-expect-error TS7006 - strict migration
                ignore: (errorEvent) => {
                  // Ignore reporting JS events from the underline RUM ResizeObserver
                  return /^(ResizeObserver loop)+/.test(errorEvent?.message);
                },
              },
            ],
            [
              'performance',
              {
                ignore: (entry: PerformanceEntry) => {
                  return (
                    entry.entryType === 'resource' &&
                    SECURITY_PARAMS.some((param) => param.test(entry.name))
                  );
                },
              },
            ],
            [
              'http',
              {
                urlsToExclude: SECURITY_PARAMS,
                recordAllRequests: false,
              },
            ],
            [
              'interaction',
              {
                enableMutationObserver: true,
                events: RUMDOMEvents,
              },
            ],
          ],
        });
      } catch (error) {
        localLog.error({ error }, 'Failed to initialise RUM');
        return;
      }

      // Apply any session attributes requested before the client existed.
      if (Object.keys(this.pendingSessionAttributes).length > 0) {
        this.rumClient.addSessionAttributes(this.pendingSessionAttributes);
        this.pendingSessionAttributes = {};
      }

      // Route error-level logs to RUM now that the client exists.
      setErrorSink((error) => {
        RumClient.recordError(error);
      });
    }

    return this.rumClient;
  }
  public static recordError(error: any) {
    if (RumClient.rumClient) {
      RumClient.rumClient.recordError(error);
    }
  }

  /**
   * Attaches the given attributes to the metadata of all subsequent RUM events
   * in the session, buffering them until the client is created.
   */
  public static addSessionAttributes(attributes: SessionAttributes) {
    // Buffer until create() exists, then flush; otherwise the attributes are lost.
    if (!RumClient.rumClient) {
      RumClient.pendingSessionAttributes = { ...RumClient.pendingSessionAttributes, ...attributes };
      return;
    }

    RumClient.rumClient.addSessionAttributes(attributes);
  }
  public static recordPageView(
    pageId: string,
    userProfile?: UserProfile,
    contentId?: string,
    collectionId?: string,
    postId?: string,
    suggestionId?: string,
  ) {
    if (RumClient.rumClient) {
      RumClient.rumClient.recordPageView({
        pageId,
        pageAttributes: {
          userId: userProfile?.userId,
          contentId,
          collectionId,
          postId,
          suggestionId,
        },
      });
    }
  }

  public static recordEvent(id: string, event: any) {
    if (RumClient.rumClient) {
      RumClient.rumClient.recordEvent(id, event);
    }
  }

  /**
   * Forces buffered events to be sent immediately via beacon.
   *
   * Use after recording an event that is followed by navigation (e.g. a
   * clickthrough to an external link), since the periodic dispatch may not
   * fire before the page is unloaded or loses focus. Beacon delivery survives
   * unload, unlike a normal request.
   */
  public static flushBeacon() {
    if (RumClient.rumClient) {
      RumClient.rumClient.dispatchBeacon();
    }
  }

  private static rumClient: AwsRum;
}

export default RumClient;
